import { useMutation, useQueryClient } from '@tanstack/react-query';

import { updateTriggerProfile } from '@/lib/api/generated/api';
import type {
  TriggerProfileView,
  UpdateTriggerScheduleRequest,
} from '@/lib/api/generated/model';

import { toTriggerCreateError } from '../errors';
import { triggerKeys } from '../queryKeys';

/**
 * Edit an existing scheduled (`github`/`gitlab`) trigger profile's cadence
 * — interval, cron expression, or timezone — without touching its enabled
 * state, connection, or config (FR-010; backend `UpdateTriggerScheduleRequest`
 * docstring: "identity, config, connection_id, and run history are all
 * untouched").
 *
 * Calls `updateTriggerProfile` — the same shared PATCH endpoint
 * `useSetTriggerProfileEnabled` calls — but with the *other* branch of its
 * `anyOf` request body. The two are separate hooks rather than one with a
 * discriminated parameter (research.md D3): the backend body has no
 * discriminator field, so a caller must send exactly one shape or the
 * other, never fields from both. This hook's mutation variable is
 * `UpdateTriggerScheduleRequest` itself, not a wrapper — `enabled` has no
 * way to end up in the request body it sends.
 */
export function useUpdateTriggerProfileSchedule(workspaceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      triggerProfileId,
      schedule,
    }: {
      triggerProfileId: string;
      schedule: UpdateTriggerScheduleRequest;
    }): Promise<TriggerProfileView> => {
      const response = await updateTriggerProfile(
        workspaceId,
        triggerProfileId,
        schedule,
      );
      if (response.status === 200) return response.data;
      throw toTriggerCreateError(response.status, response.data);
    },
    onSuccess: (_profile, variables) => {
      void queryClient.invalidateQueries({
        queryKey: triggerKeys.list(workspaceId),
      });
      void queryClient.invalidateQueries({
        queryKey: triggerKeys.detail(workspaceId, variables.triggerProfileId),
      });
    },
  });
}
